(() => {
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const initCollectionPage = (root) => {
    if (!root || root.dataset.collectionReady === 'true') return;
    root.dataset.collectionReady = 'true';

    const drawer = root.querySelector('[data-filter-drawer]');
    const overlay = root.querySelector('[data-filter-overlay]');
    const opener = root.querySelector('[data-filter-open]');
    const closer = root.querySelector('[data-filter-close]');
    const sidebar = root.querySelector('[data-filter-sidebar]');
    const desktopFilters = window.matchMedia('(min-width: 1280px)');
    let lastFocused = null;

    if (desktopFilters.matches && sidebar) opener?.setAttribute('aria-expanded', 'true');

    const openDrawer = () => {
      if (!drawer || !overlay) return;
      lastFocused = document.activeElement;
      overlay.hidden = false;
      requestAnimationFrame(() => overlay.classList.add('is-open'));
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      opener?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('collection-filter-open');
      closer?.focus();
    };

    const closeDrawer = () => {
      if (!drawer || !overlay) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      opener?.setAttribute('aria-expanded', 'false');
      overlay.classList.remove('is-open');
      document.body.classList.remove('collection-filter-open');
      window.setTimeout(() => { overlay.hidden = true; }, 260);
      lastFocused?.focus?.();
    };

    opener?.addEventListener('click', () => {
      if (desktopFilters.matches && sidebar) {
        const isHidden = sidebar.classList.toggle('is-hidden');
        opener.setAttribute('aria-expanded', String(!isHidden));
        return;
      }
      openDrawer();
    });
    closer?.addEventListener('click', closeDrawer);
    overlay?.addEventListener('click', closeDrawer);
    drawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeDrawer();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...drawer.querySelectorAll(focusableSelector)].filter((item) => item.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });

    root.querySelector('[data-collection-sort]')?.addEventListener('change', (event) => event.currentTarget.form?.submit());

    drawer?.querySelector('form')?.addEventListener('change', () => {
      const checked = drawer.querySelectorAll('input[type="checkbox"]:checked').length;
      const priceValues = [...drawer.querySelectorAll('input[type="number"]')].some((input) => input.value !== '');
      const selectedCount = checked + (priceValues ? 1 : 0);
      const countNode = root.querySelector('[data-active-filter-count]');
      const applyButton = drawer.querySelector('[data-filter-apply]');
      if (countNode) countNode.textContent = String(selectedCount);
      if (applyButton) applyButton.textContent = `${applyButton.dataset.applyLabel} (${selectedCount})`;
    });

    const quickView = root.querySelector('[data-quick-view-dialog]');
    const quickViewLoading = quickView?.querySelector('[data-quick-view-loading]');
    const quickViewContent = quickView?.querySelector('[data-quick-view-content]');
    const quickViewImage = quickView?.querySelector('[data-quick-view-image]');
    const quickViewTitle = quickView?.querySelector('[data-quick-view-title]');
    const quickViewDescription = quickView?.querySelector('[data-quick-view-description]');
    const quickViewPrice = quickView?.querySelector('[data-quick-view-price]');
    const quickViewCompare = quickView?.querySelector('[data-quick-view-compare]');
    const quickViewSaving = quickView?.querySelector('[data-quick-view-saving]');
    const quickViewVariants = quickView?.querySelector('[data-quick-view-variants]');
    const quickViewVariantsWrap = quickView?.querySelector('[data-quick-view-variants-wrap]');
    const quickViewVariantId = quickView?.querySelector('[data-quick-view-variant-id]');
    const quickViewAdd = quickView?.querySelector('[data-quick-view-add]');
    const quickViewLink = quickView?.querySelector('[data-quick-view-link]');
    const quickViewQuantity = quickView?.querySelector('[data-quick-view-quantity]');
    let quickViewProduct = null;

    const formatMoney = (cents) => {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, window.theme?.settings?.moneyFormat);
      const amount = (Number(cents || 0) / 100).toFixed(2);
      return (window.theme?.settings?.moneyFormat || '{{amount}}').replace(/\{\{\s*amount[^}]*\}\}/, amount);
    };

    const renderQuickViewVariant = (variant) => {
      if (!variant) return;
      if (quickViewVariantId) quickViewVariantId.value = variant.id;
      if (quickViewPrice) quickViewPrice.textContent = formatMoney(variant.price);
      if (quickViewCompare) {
        const onSale = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
        quickViewCompare.hidden = !onSale;
        quickViewCompare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        if (quickViewSaving) {
          const percentage = onSale ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;
          quickViewSaving.hidden = !onSale;
          quickViewSaving.textContent = onSale ? quickView.dataset.saveTemplate.replace('__PERCENT__', percentage) : '';
        }
      }
      const variantImage = variant.featured_image?.src || quickViewProduct?.featured_image;
      if (quickViewImage && variantImage) quickViewImage.src = variantImage;
      if (quickViewAdd) {
        quickViewAdd.disabled = !variant.available;
        quickViewAdd.textContent = variant.available ? quickView.dataset.addLabel : quickView.dataset.soldLabel;
      }
    };

    const openQuickView = async (button) => {
      if (!quickView) return;
      button.setAttribute('aria-busy', 'true');
      quickViewLoading.hidden = true;
      quickViewContent.hidden = true;
      try {
        const productUrl = new URL(button.dataset.productUrl, window.location.origin);
        productUrl.pathname = `${productUrl.pathname}.js`;
        const response = await fetch(productUrl);
        if (!response.ok) throw new Error('Unable to load product');
        quickViewProduct = await response.json();
        if (quickViewTitle) quickViewTitle.textContent = quickViewProduct.title;
        if (quickViewDescription) quickViewDescription.innerHTML = quickViewProduct.description || '';
        if (quickViewImage) { quickViewImage.src = quickViewProduct.featured_image || quickViewProduct.images?.[0] || ''; quickViewImage.alt = quickViewProduct.title; }
        if (quickViewLink) quickViewLink.href = button.dataset.productUrl;
        if (quickViewQuantity) quickViewQuantity.value = 1;
        if (quickViewVariants) {
          quickViewVariants.replaceChildren(...quickViewProduct.variants.map((variant) => {
            const option = document.createElement('option');
            option.value = variant.id;
            option.textContent = variant.title;
            option.disabled = !variant.available;
            return option;
          }));
          quickViewVariantsWrap.hidden = quickViewProduct.variants.length <= 1;
          quickViewVariants.value = String(quickViewProduct.variants.find((variant) => variant.available)?.id || quickViewProduct.variants[0]?.id || '');
        }
        renderQuickViewVariant(quickViewProduct.variants.find((variant) => String(variant.id) === quickViewVariants?.value) || quickViewProduct.variants[0]);
        quickViewLoading.hidden = true;
        quickViewContent.hidden = false;
        quickView.showModal();
      } catch (error) {
        window.location.href = button.dataset.productUrl;
      } finally {
        button.removeAttribute('aria-busy');
      }
    };

    root.addEventListener('click', (event) => {
      const quickViewButton = event.target.closest('[data-quick-view-open]');
      if (quickViewButton) openQuickView(quickViewButton);
    });
    quickView?.querySelector('[data-quick-view-close]')?.addEventListener('click', () => quickView.close());
    quickView?.addEventListener('click', (event) => { if (event.target === quickView) quickView.close(); });
    quickView?.addEventListener('cancel', () => quickView.close());
    quickViewVariants?.addEventListener('change', () => renderQuickViewVariant(quickViewProduct?.variants.find((variant) => String(variant.id) === quickViewVariants.value)));
    quickView?.querySelector('[data-quick-view-minus]')?.addEventListener('click', () => { quickViewQuantity.value = Math.max(1, Number(quickViewQuantity.value || 1) - 1); });
    quickView?.querySelector('[data-quick-view-plus]')?.addEventListener('click', () => { quickViewQuantity.value = Number(quickViewQuantity.value || 1) + 1; });
    quickView?.querySelector('[data-quick-view-form]')?.addEventListener('submit', () => window.setTimeout(() => quickView.close(), 350));

    root.addEventListener('click', async (event) => {
      const loadMore = event.target.closest('[data-load-more]');
      if (!loadMore) return;
      event.preventDefault();
      if (loadMore.getAttribute('aria-busy') === 'true') return;
      loadMore.setAttribute('aria-busy', 'true');
      const status = root.querySelector('[data-load-more-status]');
      if (status) status.textContent = loadMore.textContent.trim();

      try {
        const response = await fetch(loadMore.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error('Unable to load products');
        const html = await response.text();
        const nextDocument = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = nextDocument.querySelector('[data-collection-grid]');
        const currentGrid = root.querySelector('[data-collection-grid]');
        if (!nextGrid || !currentGrid) throw new Error('Product grid missing');
        [...nextGrid.children].forEach((card) => currentGrid.append(card));
        const nextButton = nextDocument.querySelector('[data-load-more]');
        if (nextButton) {
          loadMore.href = nextButton.href;
          loadMore.removeAttribute('aria-busy');
        } else {
          loadMore.closest('[data-load-more-wrap]')?.remove();
        }
        window.history.replaceState({}, '', response.url);
        if (status) status.textContent = '';
        syncCompareButtons();
      } catch (error) {
        window.location.href = loadMore.href;
      }
    });

    const compareKey = 'rosemary-collection-compare';
    const compareBar = root.querySelector('[data-compare-bar]');
    const compareItems = root.querySelector('[data-compare-items]');
    const compareCount = root.querySelector('[data-compare-count]');
    const readCompare = () => {
      try { return JSON.parse(localStorage.getItem(compareKey) || '[]'); } catch (error) { return []; }
    };
    const writeCompare = (items) => localStorage.setItem(compareKey, JSON.stringify(items.slice(0, 4)));

    const renderCompare = () => {
      if (!compareBar) return;
      const items = readCompare();
      compareBar.hidden = items.length === 0;
      if (compareCount) compareCount.textContent = `${items.length}/4`;
      if (compareItems) {
        compareItems.replaceChildren(...items.map((item) => {
          const link = document.createElement('a');
          link.href = item.url;
          link.title = item.title;
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = item.title;
          link.append(image);
          return link;
        }));
      }
    };

    const syncCompareButtons = () => {
      const ids = new Set(readCompare().map((item) => String(item.id)));
      root.querySelectorAll('[data-compare-product]').forEach((button) => button.setAttribute('aria-pressed', String(ids.has(button.dataset.productId))));
      renderCompare();
    };

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-compare-product]');
      if (!button) return;
      const items = readCompare();
      const itemIndex = items.findIndex((item) => String(item.id) === button.dataset.productId);
      if (itemIndex >= 0) items.splice(itemIndex, 1);
      else if (items.length < 4) items.push({ id: button.dataset.productId, title: button.dataset.productTitle, url: button.dataset.productUrl, image: button.dataset.productImage });
      writeCompare(items);
      syncCompareButtons();
    });

    root.querySelector('[data-compare-clear]')?.addEventListener('click', () => { writeCompare([]); syncCompareButtons(); });
    syncCompareButtons();
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-collection-page]').forEach(initCollectionPage);
  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
